/**
 * VerifyAffiliationPage — where the QR code on a Certificate of Affiliation
 * lands. No login: anybody holding the paper can check it is genuine and,
 * more importantly, whether the affiliation is still current today.
 */
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, ShieldAlert, Search, MapPin } from 'lucide-react';

import api from '@/lib/api';
import FwmShell from '@/components/fwm/FwmShell';
import { Skeleton } from '@/components/ui/skeleton';

const date = (v) => (v ? new Date(v).toLocaleDateString(undefined,
  { day: 'numeric', month: 'long', year: 'numeric' }) : '—');

export default function VerifyAffiliationPage() {
  const { serial } = useParams();

  const { data, isLoading } = useQuery({
    queryKey: ['verify-affiliation', serial],
    queryFn: () => api.get(`/affiliates/certificates/verify/${serial}`).then((r) => r.data),
    retry: false,
  });

  return (
    <FwmShell ctaLabel="Affiliate directory" ctaLink="/affiliated-ministries"
              testId="verify-affiliation-page">
      <section className="mx-auto max-w-[760px] px-6 lg:px-10 pt-14 pb-24">
        <span className="fwm-kicker">Certificate check</span>
        <h1 className="fwm-display text-4xl mt-3">Certificate of Affiliation</h1>
        <p className="mt-3 text-sm text-[color:var(--fwm-muted)]">
          Number <span className="font-mono">{serial}</span>
        </p>

        {isLoading ? (
          <Skeleton className="mt-8 h-40 w-full" />
        ) : !data?.found ? (
          <div className="fwm-card mt-8 p-8" data-testid="verify-not-found">
            <div className="flex items-center gap-3">
              <Search className="h-5 w-5 text-slate-500" />
              <p className="fwm-display text-2xl">No certificate carries that number</p>
            </div>
            <p className="mt-3 text-sm text-[color:var(--fwm-muted)]">
              Check the number printed under the affiliation details, or browse the{' '}
              <Link to="/affiliated-ministries" className="underline">public directory</Link>.
            </p>
          </div>
        ) : (
          <div className="fwm-card mt-8 p-8" data-testid="verify-result">
            <div className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold ${
              data.valid ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                         : 'border-amber-200 bg-amber-50 text-amber-800'}`}
                 data-testid="verify-badge">
              {data.valid ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
              {data.valid ? 'Affiliation is current' : `Affiliation is ${data.status}`}
            </div>

            <h2 className="fwm-display text-3xl mt-5" data-testid="verify-org-name">{data.org_name}</h2>
            {(data.city || data.country) && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-[color:var(--fwm-muted)]">
                <MapPin className="h-3.5 w-3.5" />
                {[data.city, data.country].filter(Boolean).join(' · ')}
              </p>
            )}

            <dl className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="fwm-kicker">Valid from</dt>
                <dd className="text-sm mt-1">{date(data.valid_from)}</dd>
              </div>
              <div>
                <dt className="fwm-kicker">Valid until</dt>
                <dd className="text-sm mt-1">{date(data.valid_until)}</dd>
              </div>
            </dl>

            <p className="mt-6 text-sm text-[color:var(--fwm-muted)]">{data.message}</p>
            {data.valid && data.slug && (
              <Link to={`/affiliated-ministries/${data.slug}`} className="fwm-btn mt-6 inline-flex"
                    data-testid="verify-profile-link">
                View their profile
              </Link>
            )}
          </div>
        )}
      </section>
    </FwmShell>
  );
}
